/**
 * Reading a `.env` file into the variables list.
 *
 * Two steps, both pure: `parseDotenv` turns the text into pairs, and
 * `planEnvImport` decides what each pair does to the list that is already
 * there. Nothing here touches the form — the dialog shows the plan, and the
 * screen applies it only when the reader says so.
 *
 * The file itself goes nowhere. It is read in the browser; what reaches the
 * API is the same list of names and values the form always sent.
 */

export interface DotenvEntry {
  key: string;
  value: string;
  /** 1-based line the entry starts on. */
  line: number;
  /** An unquoted value carrying a `#` that was kept as part of it. */
  hasBareHash: boolean;
}

export interface DotenvProblem {
  line: number;
  reason: string;
}

const KEY = /^[A-Za-z_][A-Za-z0-9_.-]*$/;

/**
 * The text of a `.env` file as pairs.
 *
 * The rules are the ones the common readers agree on:
 *
 *   - blank lines and lines starting with `#` are skipped;
 *   - a leading `export ` is dropped;
 *   - a value in single quotes or backticks is taken literally, one in double
 *     quotes has `\n` and `\r` turned into line breaks, and all three may run
 *     over several lines;
 *   - an unquoted value is trimmed, and ` #` (a hash after a space) starts a
 *     comment.
 *
 * And one where they do not: a `#` with no space before it. Node's dotenv cuts
 * the value there; Docker's `--env-file` and python-dotenv keep it. Cutting
 * turns `PASSWORD=ab#cd` into `ab` without a word, and the app then fails with
 * a wrong credential a long way from here — so the value is kept whole and the
 * entry is flagged, for the dialog to point at.
 *
 * When a name appears twice, the last one wins.
 */
export const parseDotenv = (text: string): { entries: DotenvEntry[]; problems: DotenvProblem[] } => {
  const lines = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').split('\n');
  const byKey = new Map<string, DotenvEntry>();
  const problems: DotenvProblem[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const lineNumber = index + 1;
    // Only the left is trimmed: a quoted value that runs on to the next line
    // owns the spaces at the end of this one.
    const line = lines[index].replace(/^\s+/, '');

    if (!line.trim() || line.startsWith('#')) {
      continue;
    }

    const body = line.replace(/^export\s+/, '');
    const equals = body.indexOf('=');

    if (equals === -1) {
      problems.push({ line: lineNumber, reason: 'not a NAME=value line' });
      continue;
    }

    const key = body.slice(0, equals).trim();

    if (!KEY.test(key)) {
      problems.push({ line: lineNumber, reason: 'the name is not a variable name' });
      continue;
    }

    const rest = body.slice(equals + 1).replace(/^\s+/, '');
    const quote = rest[0] === '"' || rest[0] === "'" || rest[0] === '`' ? rest[0] : undefined;
    let value: string | undefined;
    let hasBareHash = false;

    if (quote) {
      // The closing quote may be lines away; the lines in between are part of
      // the value exactly as written.
      //
      // Each line is looked at once, on its own. Gluing the lines into one
      // growing string and searching that from the top made a quote that
      // never closes cost the square of the file: seconds for a 64 KB paste,
      // and a frozen tab for anything larger.
      const first = rest.slice(1);
      let cursor = index;
      let tail = first;
      let close = closingQuote(tail, quote);

      while (close === -1 && cursor + 1 < lines.length) {
        cursor += 1;
        tail = lines[cursor];
        close = closingQuote(tail, quote);
      }

      const after = close === -1 ? '' : tail.slice(close + 1).trim();

      // Closed, and nothing after it but a comment: a quoted value. Anything
      // else — never closed, or text after the quote — is an unquoted value
      // that happens to start with one.
      if (close !== -1 && (!after || after.startsWith('#'))) {
        value = cursor === index
          ? first.slice(0, close)
          : [first, ...lines.slice(index + 1, cursor), tail.slice(0, close)].join('\n');

        if (quote === '"') {
          value = value.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
        }

        index = cursor;
      }
    }

    if (value === undefined) {
      const comment = rest.search(/\s#/);

      value = (comment === -1 ? rest : rest.slice(0, comment)).trim();
      hasBareHash = value.includes('#');
    }

    byKey.delete(key);
    byKey.set(key, { key, value, line: lineNumber, hasBareHash });
  }

  return { entries: Array.from(byKey.values()), problems };
};

/**
 * Where a quote closes on this line: the first one not behind a backslash. A
 * backslash at the very end covers the line break, so each line starts clean.
 */
const closingQuote = (line: string, quote: string): number => {
  for (let at = 0; at < line.length; at += 1) {
    if (line[at] === '\\') {
      at += 1;
    } else if (line[at] === quote) {
      return at;
    }
  }

  return -1;
};

/** The rule both variable forms hold a name to. */
export const ENV_NAME = /(^[A-Z_])[A-Z0-9_]+$/;

/** Mirrors `ENV_VARIABLE_MAX_LENGTH` in the API's `updateAppSettings`. */
export const ENV_VARIABLE_MAX_LENGTH = 1023;

/** The most the dialog reads, chosen or pasted. A `.env` past this is not a
 *  `.env`, and every character of it is parsed on every keystroke. */
export const ENV_FILE_MAX_BYTES = 64 * 1024;

/**
 * How many variables an import will grow the list to, and no further.
 *
 * A real `.env` has tens of lines; 64 KB of short ones is some 3,500, which
 * is a form of 3,500 rows. The ceiling is on the import alone: adding a
 * variable by hand past it is the reader's own choice, one row at a time.
 */
export const ENV_IMPORT_MAX_VARIABLES = 200;

export interface EnvPair {
  key: string;
  value: string;
}

export interface EnvImportPlan {
  /** Names the list does not have yet. */
  added: EnvPair[];
  /** Names the list has, with a different value in the file. */
  replaced: EnvPair[];
  /** Names the list has with this very value: nothing to do. */
  unchanged: string[];
  /** Names left out, and why. */
  skipped: Array<{ label: string; reason: string }>;
  /** The ones among them left out because the platform owns the name — the
   *  a skip the dialog spells out, since it is the only one where the file
   *  asked for something that was deliberately not done. */
  reserved: string[];
  /** New names left out because the list would pass
   *  `ENV_IMPORT_MAX_VARIABLES`. Replacing a value is never among them: it
   *  does not make the list any longer. */
  overLimit: number;
  /** Lines that are not a variable at all. A count: one row for each made a
   *  list as long as whatever was pasted, when what was pasted was not a
   *  `.env`. */
  unreadableLines: number;
  /** Names imported with a `#` kept in the value, to be checked by eye. */
  bareHash: string[];
}

/**
 * What importing `text` would do to `existing`.
 *
 * Importing only ever adds and replaces: a name the file does not mention is
 * left alone, so a partial file cannot empty the list. `reservedKeys` are the
 * names the platform fills in itself — the managed database's connection
 * string — and a file is never allowed to write over those.
 *
 * New names are taken in the order the file has them until the list reaches
 * `ENV_IMPORT_MAX_VARIABLES`; the rest are counted in `overLimit`.
 */
export const planEnvImport = (
  text: string,
  existing: EnvPair[],
  reservedKeys: string[] = []
): EnvImportPlan => {
  const { entries, problems } = parseDotenv(text);
  const current = new Map(existing.map((pair) => [pair.key, pair.value]));
  // Rows with a name: the blank one a form keeps open for typing is not a
  // variable yet.
  let room = Math.max(0, ENV_IMPORT_MAX_VARIABLES - existing.filter((pair) => pair.key).length);
  const plan: EnvImportPlan = {
    added: [], replaced: [], unchanged: [], skipped: [], reserved: [], overLimit: 0,
    unreadableLines: problems.length, bareHash: [],
  };

  const refusal = (key: string, value: string): string | undefined => {
    if (!ENV_NAME.test(key)) {
      return 'names use uppercase letters, numbers and _';
    }

    if (reservedKeys.includes(key)) {
      return 'set by Back4app';
    }

    if (/[\n\r]/.test(value)) {
      return 'values over several lines are not supported';
    }

    if (key.length + value.length > ENV_VARIABLE_MAX_LENGTH) {
      return `longer than ${ENV_VARIABLE_MAX_LENGTH} characters`;
    }

    return undefined;
  };

  for (const { key, value, hasBareHash } of entries) {
    const reason = refusal(key, value);

    if (reason) {
      plan.skipped.push({ label: key, reason });

      if (reservedKeys.includes(key)) {
        plan.reserved.push(key);
      }

      continue;
    }

    if (current.has(key) && (current.get(key) ?? '') === value) {
      plan.unchanged.push(key);
      continue;
    }

    if (current.has(key)) {
      plan.replaced.push({ key, value });
    } else if (room > 0) {
      room -= 1;
      plan.added.push({ key, value });
    } else {
      plan.overLimit += 1;
      continue;
    }

    if (hasBareHash) {
      plan.bareHash.push(key);
    }
  }

  return plan;
};

/**
 * Whether a paste into a name field is a `.env` rather than a name: at least
 * one `NAME=` at the start of a line. A name on its own never has an `=`.
 */
export const looksLikeDotenv = (text: string): boolean =>
  /^\s*(?:export\s+)?[A-Za-z_][A-Za-z0-9_.-]*\s*=/m.test(text);
